import React, { memo } from 'react';
import { changeBorderRadius } from '../../state/actions/change-border-radius';
import { changeItemsWithSelection } from '../../state/actions/change-items';
import { useWriteContext } from '../../utils/use-context';
import { InspectorSubLabel } from '../components/inspector-label';
import { NumberControlUpdateHandler } from './number-controls';
import { ValueInputField } from '@crabs-ai/ds-react';
import { useTranslations } from 'next-intl';
import { RoundedCorner } from '@crabs-ai/web-icons';
import { useTargetItemIds } from '../utils/use-target-item-ids';
import { useSelectionValues } from '../utils/use-selection-values';
import { EditorStarterItem } from '../../items/item-type';

const BorderRadiusControlUnmemoized: React.FC<{
  borderRadius: number;
  itemId: string;
}> = ({ borderRadius, itemId }) => {
  const t = useTranslations();
  const { setState } = useWriteContext();
  const targetIds = useTargetItemIds(itemId);
  const { isMixed } = useSelectionValues<EditorStarterItem, number>(
    itemId,
    (item) => {
      if ('borderRadius' in item) {
        return item.borderRadius;
      }

      return 0;
    },
  );

  const onRadius: NumberControlUpdateHandler = React.useCallback(
    ({ num, commitToUndoStack }) => {
      return setState({
        update: (state) => {
          return changeItemsWithSelection(state, itemId, targetIds, (i) =>
            changeBorderRadius({ item: i, borderRadius: num }),
          );
        },
        commitToUndoStack,
      });
    },
    [setState, itemId, targetIds],
  );

  return (
    <div className={'flex w-full flex-col'}>
      <InspectorSubLabel>
        {t('Editor.inspector.controls.border-radius.title')}
      </InspectorSubLabel>
      <ValueInputField
        isShowLeadingNode={true}
        leadingNodeType="icon"
        leadingNodeValue={
          <RoundedCorner className="size-[20px] [&_path]:fill-sys-cont-neutral-default" />
        }
        setValue={onRadius}
        value={borderRadius}
        min={0}
        max={null}
        step={1}
        showStepper={false}
        accessibilityLabel="Corner radius"
        hasMixedValue={isMixed}
      />
    </div>
  );
};

export const BorderRadiusControl = memo(BorderRadiusControlUnmemoized);
